/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useEffect, useState } from "react";
import Box from "components/generals/Box";
import InsuranceTransactionTable from "components/tables/InsuranceTransactionTable";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { InsuranceTransactionInterface, QueryResultInterface } from "@malmur-technologies/medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { useResetPageOnFilter } from "hooks/tableReset.hook";
import Grid from "components/generals/Grid";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import Button from "components/generals/Button";
import TextField from "@mui/material/TextField";

/**
 * Content component for displaying and managing insurance transactions.
 * Shows a table of all insurance-related financial transactions with filtering,
 * pagination, and search functionality.
 * @returns {React.ReactElement} The insurance transactions content component
 */
const InsuranceTransactionsDataContent: React.FC = () => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [InsuranceTransactionList, setInsuranceTransactionList] = useState<InsuranceTransactionInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<string>("");
  const { t } = useTranslation();
  const today = new Date();

  const [startDate, setStartDate] = useState<string>(
    new Date(today.getFullYear(), 0, 1).toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState<string>(
    new Date(today.getFullYear(), 11, 31).toISOString().split("T")[0]
  );


  const formatDateForInput = (date: string) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const clearFilters = () => {
    setStartDate(new Date(today.getFullYear(), 0, 1).toISOString().split("T")[0]);
    setEndDate(new Date(today.getFullYear(), 11, 31).toISOString().split("T")[0]);
    setFilterModel("");
    setPage(1);
  };

  const fetchInsuranceTransactions = () => {
    let url = `${CoreAPI_URLs.INSURANCE_TRANSACTION_GET_ALL}?search=${filterModel}&page=${page}&pageSize=${pageSize}`;

    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;

    sendRequest(url, onGetInsuranceTransactionHandler, "GET");
  };

  useEffect(() => {
    fetchInsuranceTransactions();
  }, [page, pageSize, filterModel, startDate, endDate]);

  useResetPageOnFilter(filterModel, page, setPage);

  const onGetInsuranceTransactionHandler = (
    InsuranceTransactionsResponse: QueryResultInterface<InsuranceTransactionInterface>
  ) => {
    setInsuranceTransactionList(InsuranceTransactionsResponse.results);
    setRowsTotal(InsuranceTransactionsResponse.total);
    setPageTotal(Math.ceil(InsuranceTransactionsResponse.total / pageSize));
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5} lg={5}>
          <TextField
            label={t("billingEncounterDataContent.startDate") || "Start Date"}
            type="date"
            value={formatDateForInput(startDate)}
            onChange={handleStartDateChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={5} lg={5}>
          <TextField
            label={t("billingEncounterDataContent.endDate") || "End Date"}
            type="date"
            value={formatDateForInput(endDate)}
            onChange={handleEndDateChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={2} lg={2} sx={{ position: "sticky", top: 0 }}>
          <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].VIEW]}
            >
              <Button type="button" variant="contained" color="dark" onClick={clearFilters}>
                {t("billingEncounterDataContent.clear")}
              </Button>
            </PermissionWrapper>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <InsuranceTransactionTable
            modelList={InsuranceTransactionList}
            setModelList={setInsuranceTransactionList}
            canSearch={true}
            canSearchPlaceholder={t("InsuranceTransactionsTable.searchPlaceholder")}
            entriesPerPage={{ defaultValue: pageSize }}
            setPageState={setPage}
            setPageSizeState={setPageSize}
            apiPagination={true}
            apiPageTotal={pageTotal}
            apiRowsTotal={rowsTotal}
            apiPageCurrentIndex={page}
            setModelFilter={setFilterModel}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsuranceTransactionsDataContent;
